import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listCampaigns, deleteCampaign } from "@/lib/api";
import { toast } from "sonner";
import { Send, Plus, Trash2, BarChart2, Pencil } from "lucide-react";

const StatusBadge = ({ status }) => {
  const map = {
    draft: "badge-neutral",
    sending: "badge-warning",
    sent: "badge-success",
    failed: "badge-error",
  };
  return <span className={`badge ${map[status] || "badge-neutral"}`}>{status}</span>;
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listCampaigns();
      setCampaigns(data);
    } catch (e) {
      toast.error("Failed to load campaigns");
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const int = setInterval(refresh, 4000); // auto-refresh for "sending" progress
    return () => clearInterval(int);
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign and all its analytics?")) return;
    try {
      await deleteCampaign(id);
      refresh();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="campaigns-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="section-label mb-2">Campaigns</div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]"
            style={{ fontFamily: "Outfit" }}
          >
            Campaigns
          </h1>
          <p className="text-sm text-[#475569] mt-2 max-w-xl">
            Compose personalized emails with placeholders like <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded text-xs">{"{{first_name}}"}</code>. Track opens & clicks in real time.
          </p>
        </div>
        <Link to="/campaigns/new" className="btn-primary inline-flex items-center gap-2" data-testid="btn-new-campaign">
          <Plus size={15} strokeWidth={2.2} /> New Campaign
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-testid="empty-campaigns">
          <div className="w-14 h-14 rounded-full bg-[#E5E9F6] flex items-center justify-center mx-auto mb-4">
            <Send size={22} className="text-[#002FA7]" strokeWidth={2} />
          </div>
          <div className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>
            No campaigns yet
          </div>
          <p className="text-sm text-[#475569] mt-1 max-w-md mx-auto">
            Create your first campaign, personalize with placeholders, and send it to your audience.
          </p>
          <Link to="/campaigns/new" className="btn-primary inline-flex items-center gap-2 mt-5" data-testid="btn-first-campaign">
            <Plus size={15} /> Create Campaign
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <tr>
                <Th>Campaign</Th>
                <Th>Status</Th>
                <Th className="text-right">Recipients</Th>
                <Th className="text-right">Sent</Th>
                <Th className="text-right">Opens</Th>
                <Th className="text-right">Clicks</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-[#E2E8F0] last:border-0 hover:bg-slate-50 transition-colors"
                  data-testid={`campaign-row-${c.id}`}
                >
                  <td className="px-6 py-4">
                    <Link to={`/campaigns/${c.id}/report`} className="text-sm font-medium text-[#0F172A] hover:text-[#002FA7]">
                      {c.name}
                    </Link>
                    <div className="text-xs text-[#94A3B8] mt-0.5 truncate max-w-lg">{c.subject}</div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={c.status} />
                    {c.status === "sending" && (
                      <div className="text-xs text-[#F59E0B] mt-1 tabular-nums">
                        {c.sent_count + c.failed_count}/{c.total_recipients}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums text-[#0F172A]">{c.total_recipients}</td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums">{c.sent_count}</td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums">{c.unique_open_count || 0}</td>
                  <td className="px-6 py-4 text-sm text-right tabular-nums">{c.unique_click_count || 0}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/campaigns/${c.id}/report`}
                        className="p-1.5 rounded-md text-[#475569] hover:text-[#002FA7] hover:bg-[#F1F5F9]"
                        title="View report"
                        data-testid={`btn-report-${c.id}`}
                      >
                        <BarChart2 size={14} />
                      </Link>
                      {c.status === "draft" && (
                        <Link
                          to={`/campaigns/${c.id}/edit`}
                          className="p-1.5 rounded-md text-[#475569] hover:text-[#002FA7] hover:bg-[#F1F5F9]"
                          title="Edit"
                          data-testid={`btn-edit-${c.id}`}
                        >
                          <Pencil size={14} />
                        </Link>
                      )}
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="p-1.5 rounded-md text-[#475569] hover:text-[#E11D48] hover:bg-[#FFF1F2]"
                        title="Delete"
                        data-testid={`btn-delete-campaign-${c.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider text-[#64748B] text-left ${className}`}>
    {children}
  </th>
);
